<template>
  <modal
    name="editor-view-sql"
    width="92%"
    height="auto"
    :lazy="true"
    :scrollable="true"
    :clickToClose="false"
    @opened="onOpened"
    @closed="onClosed"
  >
    <CloseModal :close="fechar" />

    <!-- Cabeçalho -->
    <div class="ev-header">
      <div class="ev-title">
        <span class="ev-kicker">EDITAR VIEW</span>
        <span class="ev-name">{{ form.view_name || 'Nova view' }}</span>
      </div>
      <div class="ev-ativo">
        <span class="ev-ativo-label">Ativo</span>
        <div class="seg">
          <button
            type="button"
            class="seg-btn"
            :class="{ 'seg-btn--sim': Number(form.ativo) === 1 }"
            @click="form.ativo = 1"
          >Sim</button>
          <button
            type="button"
            class="seg-btn"
            :class="{ 'seg-btn--nao': Number(form.ativo) === 0 }"
            @click="form.ativo = 0"
          >Não</button>
        </div>
      </div>
    </div>

    <!-- Corpo: dois painéis -->
    <div class="ev-body">
      <!-- Painel lateral -->
      <aside class="ev-side">
        <div class="form-group">
          <label class="ev-label">
            <span class="req">*</span> Nome da view
          </label>
          <div class="ev-autocomplete">
            <input
              type="text"
              class="form-control ev-mono"
              v-model.trim="form.view_name"
              :readonly="action === 'update'"
              placeholder="Ex.: V_BDTI_CIDADE"
              autocomplete="off"
              @focus="abrirSugestoes"
              @input="abrirSugestoes(); nomeTocado = true"
              @blur="fecharSugestoesComDelay(); nomeTocado = true"
            />
            <ul
              v-if="action === 'store' && mostrarSugestoes && sugestoes.length"
              class="ev-sugestoes"
            >
              <li
                v-for="v in sugestoes"
                :key="v.view_name"
                class="ev-sugestao"
                @mousedown.prevent="selecionarView(v)"
              >
                <span class="ev-sug-nome">{{ v.view_name }}</span>
                <span class="ev-sug-tabela">{{ v.tabela }}</span>
              </li>
            </ul>
          </div>
          <!-- Só acusa erro depois de o usuário interagir com o campo -->
          <small v-if="nomeInvalido && nomeTocado" class="ev-erro">Informe o nome da view.</small>
        </div>

        <div class="form-group">
          <label class="ev-label">Descrição da view</label>
          <textarea class="form-control" rows="4" v-model="form.descricao"></textarea>
        </div>

        <CamposAccordion
          :campos="camposDaView"
          :sql="form.sql"
          @inserir="inserirCampo"
        />

        <div class="ev-ultima">
          <div class="ev-ultima-label">ÚLTIMA ALTERAÇÃO</div>
          <div class="ev-ultima-valor">{{ ultimaAlteracao }}</div>
          <LogAlteracaoDados
            tabela="sql_erp"
            :chave="form.view_name"
            :registro-label="form.view_name"
            :restorable="true"
            instancia="editor"
            @restaurar="onRestaurar"
          >
            <template #trigger="{ open }">
              <a class="ev-hist-link" :class="{ disabled: !form.view_name }" @click.prevent="form.view_name && open()">
                <fa-icon icon="clipboard-list" /> Ver histórico de alterações
              </a>
            </template>
          </LogAlteracaoDados>
        </div>
      </aside>

      <!-- Painel principal -->
      <section class="ev-main">
        <label class="ev-label">Consulta SQL</label>
        <div class="ev-editor-wrap">
          <SqlEditor ref="sqlEditor" v-model="form.sql" :campos="camposDaView" :tipo="form.tipo" @executar="$emit('executar', form.sql)" />
        </div>
      </section>
    </div>

    <!-- Rodapé -->
    <div class="ev-footer">
      <button type="button" class="ev-btn ev-btn--teal" @click="$emit('executar', form.sql)">
        <fa-icon icon="play" /> Executar SQL
      </button>
      <button type="button" class="ev-btn ev-btn--ghost" @click="fechar">Cancelar</button>
      <button type="button" class="ev-btn ev-btn--primary" :disabled="nomeInvalido" @click="salvar">
        <fa-icon icon="save" /> Salvar alterações
      </button>
    </div>
  </modal>
</template>

<script>
import CloseModal from "@/components/CloseModal";
import SqlEditor from "@/components/form/SqlEditor.vue";
import CamposAccordion from "@/views/SqlErpIntegracao/sqls/CamposAccordion.vue";
import LogAlteracaoDados from "@/components/Log/LogAlteracaoDados.vue";
import { toDateTime } from "@/helpers/utils";

const FORM_VAZIO = () => ({
  view_name: "",
  descricao: "",
  ativo: 1,
  sql: "",
  tipo: "sql",
  banco: "erp",
});

export default {
  name: "EditorViewSql",
  components: { CloseModal, SqlEditor, CamposAccordion, LogAlteracaoDados },
  props: {
    viewsSincronizaveis: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: FORM_VAZIO(),
      action: "store",
      original: null,
      baseline: null,
      nomeTocado: false,
      mostrarSugestoes: false,
    };
  },
  computed: {
    nomeInvalido() {
      return !String(this.form.view_name || "").trim();
    },
    sugestoes() {
      const termo = String(this.form.view_name || "").toUpperCase();
      const lista = this.viewsSincronizaveis || [];
      if (!termo) return lista;
      return lista.filter(
        (v) =>
          String(v.view_name || "").toUpperCase().includes(termo) ||
          String(v.tabela || "").toUpperCase().includes(termo)
      );
    },
    camposDaView() {
      const alvo = String(this.form.view_name || "").toUpperCase();
      const view = (this.viewsSincronizaveis || []).find(
        (v) => String(v.view_name || "").toUpperCase() === alvo
      );
      return view ? view.campos : [];
    },
    ultimaAlteracao() {
      const data = this.original && this.original.datahora_atualizacao;
      if (!data) return "—";
      const quem =
        (this.original.colaborador && this.original.colaborador.nome) ||
        this.original.colaborador_id_atualizacao;
      return toDateTime(data) + (quem ? " · " + quem : "");
    },
  },
  beforeDestroy() {
    // Redundância defensiva: se o componente morrer com o modal aberto.
    document.removeEventListener("keyup", this.handleEsc);
  },
  methods: {
    // O Esc só é observado enquanto o editor está aberto (eventos do vue-js-modal).
    onOpened() {
      document.addEventListener("keyup", this.handleEsc);
    },
    onClosed() {
      document.removeEventListener("keyup", this.handleEsc);
    },
    // Esc fecha o editor como se fosse "Cancelar", mas só quando ele é o único
    // modal aberto: se houver outro por cima (ex.: resultado da consulta), o
    // modal do topo trata o Esc e este é ignorado.
    handleEsc(e) {
      if (e.key === "Escape") {
        const abertos = document.querySelectorAll(".v--modal-overlay");
        if (abertos.length <= 1) {
          this.fechar();
        }
      }
    },
    abrir(item) {
      const ehUpdate = !!(item && item.view_name);
      this.action = ehUpdate ? "update" : "store";
      this.original = item || null;
      this.form = {
        ...FORM_VAZIO(),
        ...(item || {}),
        ativo: item && item.ativo != null ? Number(item.ativo) : 1,
      };
      this.nomeTocado = false;
      this.$modal.show("editor-view-sql");
      // Baseline síncrono do payload normalizado (objeto plano com chaves em
      // ordem fixa): detecta modificações não salvas sem depender de timers.
      this.baseline = JSON.stringify(this.payloadEdicao());
    },
    estaSujo() {
      if (this.baseline == null) return false;
      return JSON.stringify(this.payloadEdicao()) !== this.baseline;
    },
    fechar() {
      if (this.estaSujo()) {
        this.$swal({
          title: "Descartar alterações?",
          text: "Há modificações não salvas que serão perdidas.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Descartar",
          cancelButtonText: "Continuar editando",
        }).then((res) => {
          if (res && (res.isConfirmed || res.value)) this.fecharForcado();
        });
        return;
      }
      this.fecharForcado();
    },
    fecharForcado() {
      this.$modal.hide("editor-view-sql");
    },
    /**
     * Payload explícito, só com os campos editáveis do formulário (os mesmos
     * de FORM_VAZIO). Evita vazar para o backend campos carregados junto do
     * item (tarefa, colaborador, datas de auditoria, etc.).
     */
    payloadEdicao() {
      const f = this.form;
      return {
        view_name: String(f.view_name || "").trim(),
        descricao: f.descricao || "",
        ativo: Number(f.ativo),
        sql: f.sql || "",
        // Preserva o `tipo` persistido (fonte de verdade); não recalcula por conteúdo.
        tipo: f.tipo || "sql",
        banco: f.banco || "erp",
      };
    },
    salvar() {
      if (this.nomeInvalido) return;
      this.$emit("salvar", this.payloadEdicao(), this.action);
    },
    onRestaurar(sql) {
      // Restaurar versão: carrega o SQL escolhido no editor para revisão.
      this.form.sql = sql;
    },
    abrirSugestoes() {
      if (this.action === "store") this.mostrarSugestoes = true;
    },
    fecharSugestoesComDelay() {
      // Delay para permitir o clique numa sugestão antes de fechar.
      setTimeout(() => { this.mostrarSugestoes = false; }, 150);
    },
    selecionarView(v) {
      this.form.view_name = v.view_name;
      this.mostrarSugestoes = false;
    },
    inserirCampo(nome) {
      // Insere o campo como alias em maiúsculo (AS NOME) na posição do cursor.
      if (this.$refs.sqlEditor) {
        this.$refs.sqlEditor.inserirNoCursor(" AS " + String(nome).toUpperCase());
      }
    },
  },
};
</script>

<style scoped>
.ev-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
  border-bottom: 1px solid #e4e7ec;
}

.ev-title { display: flex; flex-direction: column; gap: 2px; }
.ev-kicker { font-size: 11px; font-weight: 700; letter-spacing: 0.12em; color: #949aa8; }
.ev-name {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 23px;
  font-weight: 700;
  color: #3a3e81;
}

.ev-ativo { display: flex; align-items: center; gap: 10px; }
.ev-ativo-label { font-size: 13px; color: #5a6072; }

.seg {
  display: inline-flex;
  background: #f6f7f9;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  overflow: hidden;
}
.seg-btn {
  border: none;
  background: transparent;
  padding: 5px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #5a6072;
  cursor: pointer;
}
.seg-btn--sim { background: #e7f6ec; color: #2a8a52; }
.seg-btn--nao { background: #fdeaea; color: #c0392b; }

.ev-body {
  display: grid;
  grid-template-columns: 320px 1fr;
}

.ev-side {
  background: #fafbfc;
  border-right: 1px solid #e4e7ec;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 72vh;
  overflow-y: auto;
}

.ev-main {
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ev-label {
  font-size: 13px;
  font-weight: 600;
  color: #2c2e38;
  margin-bottom: 6px;
  display: block;
}

.req { color: #e23b3b; }

.ev-mono { font-family: SFMono-Regular, Menlo, Consolas, monospace; }

.ev-erro { color: #e23b3b; font-size: 12px; }

.ev-autocomplete { position: relative; }

.ev-sugestoes {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 50;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid #d3d8e0;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  max-height: 240px;
  overflow-y: auto;
}

.ev-sugestao {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
}

.ev-sugestao:hover { background: #eef0f3; }

.ev-sug-nome {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  color: #2c2e38;
}

.ev-sug-tabela {
  font-size: 11px;
  color: #949aa8;
  white-space: nowrap;
}

.ev-editor-wrap {
  height: 62vh;
  min-height: 360px;
}

.ev-ultima {
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid #e4e7ec;
}
.ev-ultima-label { font-size: 11px; font-weight: 700; letter-spacing: 0.08em; color: #949aa8; }
.ev-ultima-valor { font-size: 12.5px; color: #5a6072; margin: 3px 0 8px; }

.ev-hist-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #1a7a83;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.ev-hist-link.disabled { color: #b8bcc6; cursor: not-allowed; }

.ev-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  background: #f6f7f9;
  border-top: 1px solid #e4e7ec;
}

/* Prefixo ev- para nao colidir com as classes .btn-* do Bootstrap/CoreUI */
.ev-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.ev-btn--teal { background: #fff; color: #1a7a83; border: 1px solid #c5e6e9; }
.ev-btn--ghost { background: #fff; color: #5a6072; border: 1px solid #d3d8e0; }
.ev-btn--primary { background: #3a3e81; color: #fff; border: 1px solid #2e3268; }
.ev-btn--primary:disabled { opacity: 0.5; cursor: not-allowed; }

@media (max-width: 991.98px) {
  .ev-body { grid-template-columns: 1fr; }
  .ev-side { border-right: none; border-bottom: 1px solid #e4e7ec; max-height: none; }
  .ev-editor-wrap { height: 48vh; }
}
</style>
